// Card.js
import React from 'react';
import { withImageFallback } from "../utility/imageFallback";
import { ArrowUpRight, MapPin, Star } from "lucide-react";
import CompareToggle from "./CompareToggle";

const StarRating = ({ rating }) => {
  const safeRating = Number.isFinite(Number(rating)) ? Number(rating) : 0;
  const filledStars = Math.max(0, Math.min(5, Math.floor(safeRating)));

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, index) => {
        const filled = index < filledStars;
        return (
          <Star
            key={index}
            className={
              "h-4 w-4 " +
              (filled
                ? "text-amber-500 fill-amber-500"
                : "text-slate-300")
            }
          />
        );
      })}
    </div>
  );
};

const Card = ({ title, location, learnMoreLink, footerText, totalFees, ratings, description, imageURL, collegePageUrl, college}) => {
  const fallbackLogo = "/images/college-logo.jpeg";
  const resolvedImage = imageURL !== 'N/A' && imageURL ? imageURL : fallbackLogo;
  const isClickable = Boolean(collegePageUrl);

  const navigateToCollege = () => {
    if (!collegePageUrl) return;
    window.location.href = collegePageUrl;
  };

  const onCardClick = (e) => {
    if (!isClickable) return;
    if (e.defaultPrevented) return;
    const target = e.target;
    // Don't hijack clicks on interactive elements inside the card.
    if (target && typeof target.closest === "function") {
      if (target.closest("a,button,input,select,textarea,label")) return;
    }
    navigateToCollege();
  };

  const onCardKeyDown = (e) => {
    if (!isClickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigateToCollege();
    }
  };

  return (
    <div
      className={
        "w-full rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition overflow-hidden" +
        (isClickable ? " cursor-pointer" : "")
      }
      onClick={onCardClick}
      onKeyDown={onCardKeyDown}
      role={isClickable ? "link" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable && title ? `Open ${title}` : undefined}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 p-0.5 flex-none">
            <div className="h-full w-full rounded-[11px] bg-white grid place-items-center overflow-hidden">
              <img
                src={resolvedImage}
                alt={title}
                className="h-10 w-10 object-contain"
                onError={withImageFallback(fallbackLogo)}
                loading="lazy"
              />
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              {title && (
                <div className="font-bold text-slate-900 leading-snug">
                  {collegePageUrl ? (
                    <a
                      href={collegePageUrl}
                      className="hover:text-brand-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {title}
                    </a>
                  ) : (
                    <span>{title}</span>
                  )}
                </div>
              )}
              {college?.id && <CompareToggle college={college} className="shrink-0" />}
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
              {location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {location}
                </span>
              )}
              {learnMoreLink && (
                <a
                  href={learnMoreLink}
                  className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:text-brand-800"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              )}
            </div>

            {(totalFees || ratings) && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {totalFees && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs font-semibold text-slate-600">Total Fees</div>
                    <div className="text-sm font-semibold text-slate-900 truncate">{totalFees}</div>
                  </div>
                )}
                {ratings && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs font-semibold text-slate-600">Ratings</div>
                    <div className="mt-1">
                      <StarRating rating={ratings} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {description && (
          <div
            className="mt-3 text-sm text-slate-600 line-clamp-3"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>

      {footerText && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs font-medium text-slate-600">
          {footerText}
        </div>
      )}
    </div>
  );
};

export default Card;
